import axios from "axios";
import { errorView } from "../helpers/ErrorHandler";
import {
  getAccessToken,
  getAccessTokenExpiry,
  setAccessToken,
  subscribeAccessToken
} from "../utils/authTokenStore";
import { endSession } from "../utils/session";
import { axiosAuth } from "./apiService";

// Refresh this many seconds before the access token expires.
const REFRESH_LEAD_SECONDS = 60;
const MIN_REFRESH_DELAY_MS = 5000;
const REFRESH_TIMEOUT_MS = 15000;

export type RefreshResult =
  | { status: "ok"; token: string }
  | { status: "invalid" }
  | { status: "offline" };

let inFlightRefresh: Promise<RefreshResult> | null = null;

const requestRefresh = async (): Promise<RefreshResult> => {
  try {
    // The refresh token travels only in the httpOnly cookie; the body stays empty.
    const response = await axiosAuth.post("/auth/refresh-token", undefined, {
      // A stalled request must fall into the offline path, not hold the cross-tab lock forever.
      timeout: REFRESH_TIMEOUT_MS
    });
    const token = response.data?.data?.token;
    if (token) {
      setAccessToken(token);
      return { status: "ok", token };
    }
    return { status: "invalid" };
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    // No response at all while the browser is online and there is no session to keep: the
    // request was blocked (e.g. CORS on a 401) or the server is unreachable. Sending the user to
    // sign-in beats a dead end; with a live token we keep the session and retry instead.
    if (
      axios.isAxiosError(error) &&
      !error.response &&
      !getAccessToken() &&
      (typeof navigator === "undefined" || navigator.onLine !== false)
    ) {
      return { status: "invalid" };
    }
    return status === 401 || status === 403 ? { status: "invalid" } : { status: "offline" };
  }
};

// The refresh cookie rotates and is shared by every tab, so tabs must refresh one at a time: a
// tab that sends an already-rotated cookie would get a 401. Where Web Locks are unavailable,
// only the per-tab de-duplication below applies.
const requestRefreshExclusive = (): Promise<RefreshResult> =>
  typeof navigator !== "undefined" && navigator.locks
    ? navigator.locks.request("np-auth-refresh", requestRefresh)
    : requestRefresh();

// Concurrent callers in this tab share a single refresh request; other tabs are serialised by
// the lock above.
export const refreshSession = (): Promise<RefreshResult> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestRefreshExclusive().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

let loggingOut = false;

// Test-only: module state would otherwise leak between tests.
export const __resetForTests = () => {
  loggingOut = false;
  offlineAttempts = 0;
};

// Ends an expired session locally and returns the user to sign-in. Concurrent failures (several
// requests resolving to an invalid session at once) end it only once. Other tabs are not
// signalled: they find out on their own next refresh.
export const handleLogout = () => {
  if (loggingOut) return;
  loggingOut = true;
  errorView("Session expired, please login again");
  endSession({ expired: true, redirect: true });
};

export const getValidAccessToken = async () => {
  const token = getAccessToken();
  if (!token) return null;
  const expiresAt = getAccessTokenExpiry();
  const nowSeconds = Date.now() / 1000;
  if (expiresAt && expiresAt > nowSeconds + 30) {
    return token;
  }
  if (!expiresAt) return token; // expiry unknown: rely on the 401 retry path
  const result = await refreshSession();
  if (result.status === "ok") return result.token;
  if (result.status === "invalid") {
    // The refresh cookie is rejected (401/403): the session is over, so return to sign-in
    // instead of sending the request without auth.
    handleLogout();
    return null;
  }
  // Server unreachable: the token is still unexpired, so use it rather than send no auth.
  return token;
};

// Used by the 401 interceptor. Returns the new token, null when the session is invalid
// (the user is signed out), and throws when the server could not be reached.
export const refreshAccessToken = async (): Promise<string | null> => {
  const result = await refreshSession();
  if (result.status === "ok") return result.token;
  if (result.status === "invalid") {
    handleLogout();
    return null;
  }
  throw new Error("Unable to reach the server to refresh the session");
};

export const handleInvalidTokens = () => {
  handleLogout();
};

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let offlineAttempts = 0;
const MAX_OFFLINE_DELAY_MS = 60000;

const scheduleRefresh = (delay: number) => {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(async () => {
    const result = await refreshSession();
    if (result.status === "invalid") {
      handleLogout();
    } else if (result.status === "offline") {
      // Retry with backoff; a token change (a successful refresh) resets the schedule.
      offlineAttempts += 1;
      scheduleRefresh(Math.min(MIN_REFRESH_DELAY_MS * 2 ** (offlineAttempts - 1), MAX_OFFLINE_DELAY_MS));
    }
  }, delay);
};

// Refresh shortly before expiry so long-lived work (sockets, long requests) never hits an
// expired token. The 401 retry in apiService remains the fallback.
subscribeAccessToken(token => {
  offlineAttempts = 0;
  if (token) loggingOut = false; // a new sign-in re-arms the guard
  if (refreshTimer) clearTimeout(refreshTimer);
  const expiresAt = getAccessTokenExpiry();
  if (!token || !expiresAt) return;
  scheduleRefresh(
    Math.max((expiresAt - REFRESH_LEAD_SECONDS) * 1000 - Date.now(), MIN_REFRESH_DELAY_MS)
  );
});
